import { invokeFunction } from '../api'
import { toApiRequestError } from '../errors'

export type SmeFeedbackCategory =
  | 'bug'
  | 'workflow_confusion'
  | 'missing_data'
  | 'content_issue'
  | 'visual_issue'
  | 'other'

export type SmeFeedbackSeverity = 'blocker' | 'high' | 'medium' | 'low'
export type SmeFeedbackStatus = 'new' | 'triaged' | 'accepted' | 'fixed' | 'verified' | 'closed'

export interface SmeFeedbackPin {
  id: string
  x: number
  y: number
  note: string
  created_at: string
}

export interface SmeFeedbackScreenshot {
  data_url: string
  content_type: string
  size_bytes: number
}

export interface SmeFeedbackInput {
  page_path: string
  route_name?: string | null
  facility_id?: string | null
  visit_id?: string | null
  category: SmeFeedbackCategory
  severity: SmeFeedbackSeverity
  note: string
  pins: SmeFeedbackPin[]
  screenshot?: SmeFeedbackScreenshot | null
  page_metadata: Record<string, unknown>
}

export interface SmeFeedbackItem extends SmeFeedbackInput {
  feedback_id: string
  tenant_id: string
  submitted_by: string
  status: SmeFeedbackStatus
  screenshot_bucket: string | null
  screenshot_path: string | null
  screenshot_content_type: string | null
  screenshot_size_bytes: number | null
  triaged_by: string | null
  triaged_at: string | null
  created_at: string
  updated_at: string
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function parsePins(value: unknown): SmeFeedbackPin[] {
  if (!Array.isArray(value)) return []
  return value
    .filter(isRecord)
    .map((pin) => ({
      id: typeof pin.id === 'string' ? pin.id : crypto.randomUUID(),
      x: typeof pin.x === 'number' ? pin.x : 0,
      y: typeof pin.y === 'number' ? pin.y : 0,
      note: typeof pin.note === 'string' ? pin.note : '',
      created_at: typeof pin.created_at === 'string' ? pin.created_at : '',
    }))
}

function parseFeedback(value: unknown): SmeFeedbackItem {
  if (!isRecord(value)) throw new Error('Malformed SME feedback response from server')
  if (typeof value.feedback_id !== 'string') throw new Error('Malformed SME feedback response from server')
  if (typeof value.tenant_id !== 'string') throw new Error('Malformed SME feedback response from server')
  if (typeof value.submitted_by !== 'string') throw new Error('Malformed SME feedback response from server')
  if (typeof value.page_path !== 'string') throw new Error('Malformed SME feedback response from server')
  if (typeof value.category !== 'string') throw new Error('Malformed SME feedback response from server')
  if (typeof value.severity !== 'string') throw new Error('Malformed SME feedback response from server')
  if (typeof value.status !== 'string') throw new Error('Malformed SME feedback response from server')
  if (typeof value.note !== 'string') throw new Error('Malformed SME feedback response from server')
  if (typeof value.created_at !== 'string') throw new Error('Malformed SME feedback response from server')
  if (typeof value.updated_at !== 'string') throw new Error('Malformed SME feedback response from server')

  return {
    feedback_id: value.feedback_id,
    tenant_id: value.tenant_id,
    submitted_by: value.submitted_by,
    page_path: value.page_path,
    route_name: typeof value.route_name === 'string' ? value.route_name : null,
    facility_id: typeof value.facility_id === 'string' ? value.facility_id : null,
    visit_id: typeof value.visit_id === 'string' ? value.visit_id : null,
    category: value.category as SmeFeedbackCategory,
    severity: value.severity as SmeFeedbackSeverity,
    status: value.status as SmeFeedbackStatus,
    note: value.note,
    pins: parsePins(value.pins),
    screenshot: null,
    page_metadata: isRecord(value.page_metadata) ? value.page_metadata : {},
    screenshot_bucket: typeof value.screenshot_bucket === 'string' ? value.screenshot_bucket : null,
    screenshot_path: typeof value.screenshot_path === 'string' ? value.screenshot_path : null,
    screenshot_content_type: typeof value.screenshot_content_type === 'string' ? value.screenshot_content_type : null,
    screenshot_size_bytes: typeof value.screenshot_size_bytes === 'number' ? value.screenshot_size_bytes : null,
    triaged_by: typeof value.triaged_by === 'string' ? value.triaged_by : null,
    triaged_at: typeof value.triaged_at === 'string' ? value.triaged_at : null,
    created_at: value.created_at,
    updated_at: value.updated_at,
  }
}

export async function getSmeFeedbackScreenshotUrl(feedbackId: string): Promise<string> {
  const { data, error } = await invokeFunction<unknown>('sme-feedback', {
    method: 'GET',
    path: `/${feedbackId}/screenshot`,
  })
  if (error) throw toApiRequestError(error, 'Failed to load SME feedback screenshot')
  if (!isRecord(data) || typeof data.signed_url !== 'string') {
    throw new Error('Malformed SME feedback screenshot response from server')
  }
  return data.signed_url
}

export async function createSmeFeedback(input: SmeFeedbackInput): Promise<SmeFeedbackItem> {
  const { data, error } = await invokeFunction<unknown>('sme-feedback', {
    method: 'POST',
    path: '/',
    body: input,
  })
  if (error) throw toApiRequestError(error, 'Failed to submit SME feedback')
  if (!isRecord(data)) throw new Error('Malformed SME feedback response from server')
  return parseFeedback(data.feedback)
}

export async function listSmeFeedback(status?: SmeFeedbackStatus): Promise<SmeFeedbackItem[]> {
  const params = new URLSearchParams()
  if (status) params.set('status', status)
  const query = params.toString()
  const { data, error } = await invokeFunction<unknown>('sme-feedback', {
    method: 'GET',
    path: query ? `/?${query}` : '/',
  })
  if (error) throw toApiRequestError(error, 'Failed to load SME feedback')
  if (!isRecord(data) || !Array.isArray(data.feedback)) throw new Error('Malformed SME feedback list from server')
  return data.feedback.map(parseFeedback)
}

export async function updateSmeFeedbackStatus(feedbackId: string, status: SmeFeedbackStatus): Promise<SmeFeedbackItem> {
  const { data, error } = await invokeFunction<unknown>('sme-feedback', {
    method: 'PATCH',
    path: `/${feedbackId}`,
    body: { status },
  })
  if (error) throw toApiRequestError(error, 'Failed to update SME feedback')
  if (!isRecord(data)) throw new Error('Malformed SME feedback update from server')
  return parseFeedback(data.feedback)
}

export async function promoteSmeFeedback(feedbackId: string): Promise<{ feedback: SmeFeedbackItem; github_issue_url: string }> {
  const { data, error } = await invokeFunction<unknown>('sme-feedback', {
    method: 'POST',
    path: `/${feedbackId}/promote`,
  })
  if (error) throw toApiRequestError(error, 'Failed to promote SME feedback')
  if (!isRecord(data) || typeof data.github_issue_url !== 'string') {
    throw new Error('Malformed SME feedback promotion response from server')
  }
  return {
    feedback: parseFeedback(data.feedback),
    github_issue_url: data.github_issue_url,
  }
}
